import React, { useState, useEffect } from 'react';
import { usePageTracking } from '../../../hooks/usePageTracking';
import {
  getClassifiedAdPlatforms,
  getClassifiedAdPrefill,
  generateClassifiedAdCopies,
  getClassifiedAdListings,
  getClassifiedAdListingsByJob,
  updateListingUrl,
  updateListingContent,
  downloadSubmissionPackage,
  getClassifiedAdJob,
  getActiveClassifiedAdJob,
  completeClassifiedAdListing,
  getListingImageProxyUrl,
  type Platform,
  type ClassifiedAdListing,
  type PrefillData,
} from '../../../services/classifiedAdsService';
import { checkDomainAge } from '../../../services/pdfSubmissionService';
import { initiateClassifiedAdsScan, getClassifiedAdsScanStatus } from '../../../services/discoveryService';

interface ClassifiedAdsPageProps {
  onBack: () => void;
  darkMode: boolean;
}

const PLATFORM_ICONS: Record<string, string> = {
  craigslist: 'ri-price-tag-3-line',
  gumtree:    'ri-leaf-line',
  olx:        'ri-auction-line',
  locanto:    'ri-community-line',
  kijiji:     'ri-map-pin-2-line',
  oodle:      'ri-search-eye-line',
};

const PLATFORM_COLORS: Record<string, string> = {
  craigslist: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-100 dark:border-purple-800',
  gumtree:    'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-100 dark:border-green-800',
  olx:        'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-800',
  locanto:    'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-100 dark:border-orange-800',
  kijiji:     'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-100 dark:border-red-800',
  oodle:      'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800',
};

const STATUS_BADGE: Record<string, string> = {
  draft:   'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  pending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  live:    'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};


export default function ClassifiedAdsPage({ onBack, darkMode }: ClassifiedAdsPageProps) {
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: 'Implementation / Classified Ads',
    routePath: '/dashboard#classified-ads',
  });

  const userId = localStorage.getItem('user_id') || '';

  // ── Core state ────────────────────────────────────────────────────────────
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [prefill, setPrefill] = useState<PrefillData | null>(null);
  const [keyword, setKeyword] = useState<string>('');
  const [keywordSearch, setKeywordSearch] = useState<string>('');

  // ── Generated listing (General) ───────────────────────────────────────────
  const [generalListing, setGeneralListing] = useState<ClassifiedAdListing | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [adExpanded, setAdExpanded] = useState(false);

  // ── Checked checklist state ────────────────────────────────────────────────
  const [checkedPlatforms, setCheckedPlatforms] = useState<Record<string, boolean>>({});
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [markCompleteSuccess, setMarkCompleteSuccess] = useState(false);

  // ── Edit Ad State ──────────────────────────────────────────────────────────
  const [isEditingAd, setIsEditingAd] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [savingAd, setSavingAd] = useState(false);


  // ── Shared ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryListings, setLibraryListings] = useState<ClassifiedAdListing[]>([]);
  const [selectedLibraryAd, setSelectedLibraryAd] = useState<ClassifiedAdListing | null>(null);
  const [editingLibraryUrl, setEditingLibraryUrl] = useState<string>('');
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [libraryUrlError, setLibraryUrlError] = useState<string | null>(null);
  const [savingLibraryUrl, setSavingLibraryUrl] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [hasDefaultSelected, setHasDefaultSelected] = useState(false);

  // ── Discovery & Domain Maturity ──────────────────────────────────────────
  const [mode, setMode] = useState<'new_website' | 'existing_website'>('new_website');
  const [isNewWebsite, setIsNewWebsite] = useState<boolean | null>(null);
  const [domainAgeLoading, setDomainAgeLoading] = useState<boolean>(true);
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditScanId, setAuditScanId] = useState<string | null>(null);
  const [discoveredAssets, setDiscoveredAssets] = useState<any[]>([]);
  const [auditCompleted, setAuditCompleted] = useState(false);

  const pollRef = React.useRef<any>(null);

  const pollJobStatus = (jobId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const job = await getClassifiedAdJob(jobId);
        if (job.status === 'completed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setGenerating(false);
          setLoading(false);
          await loadListings(jobId);
          await fetchLibrary();
        } else if (job.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setGenerating(false);
          setLoading(false);
          setError('Generation failed. Please try again.');
        }
      } catch (e: any) {
        if (pollRef.current) clearInterval(pollRef.current);
        setGenerating(false);
        setLoading(false);
        setError('Failed to check generation progress.');
      }
    }, 3000);
  };

  // Load platforms & prefill on mount
  useEffect(() => {
    if (!userId) return;

    const init = async () => {
      try {
        const [plats, pre] = await Promise.all([
          getClassifiedAdPlatforms(),
          getClassifiedAdPrefill(userId),
        ]);
        const sortedPlats = [...plats].sort((a, b) => b.da - a.da).slice(0, 6);
        setPlatforms(sortedPlats);
        setPrefill(pre);

        // Check domain age
        if (pre && pre.website_url) {
          try {
            setDomainAgeLoading(true);
            const ageRes = await checkDomainAge(pre.website_url);
            setIsNewWebsite(ageRes.is_new_website);
            const detectedMode = ageRes.is_new_website ? 'new_website' : 'existing_website';
            setMode(detectedMode);
          } catch (ageErr) {
            console.error('Error checking domain age:', ageErr);
          } finally {
            setDomainAgeLoading(false);
          }
        } else {
          setDomainAgeLoading(false);
        }

        const savedListing = localStorage.getItem(`classified_general_listing_${userId}`);
        let restoredKeyword = '';
        if (savedListing) {
          try {
            const parsed = JSON.parse(savedListing);
            if (parsed.primary_keyword) restoredKeyword = parsed.primary_keyword;
          } catch (e) {}
        }

        if (restoredKeyword) {
          setKeyword(restoredKeyword);
        } else if (pre.keywords.length > 0) {
          setKeyword(pre.keywords[0]);
        }

        // Resume any in-progress or completed generation job
        const activeJob = await getActiveClassifiedAdJob(userId);
        if (activeJob && activeJob.status === 'processing') {
          setActiveJobId(activeJob.id);
          setKeyword(activeJob.keyword);
          setLoading(true);
          setGenerating(true);
          pollJobStatus(activeJob.id);
        }
        await fetchLibrary();
      } catch (e: any) {
        setError('Failed to load data. Please try again.');
      } finally {
        setInitialLoading(false);
      }
    };

    init();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [userId]);

  // Poll for discovery audit results
  useEffect(() => {
    if (!auditScanId || !isAuditing) return;
    const interval = setInterval(async () => {
      try {
        const statusData = await getClassifiedAdsScanStatus(auditScanId);
        if (statusData.status === 'COMPLETED') {
          if (statusData.assets) {
            setDiscoveredAssets(statusData.assets);
          }
          setIsAuditing(false);
          setAuditCompleted(true);
        } else if (statusData.status === 'FAILED') {
          setIsAuditing(false);
          setAuditCompleted(true);
          console.error('Audit failed:', statusData.error_detail);
        }
      } catch (err) {
        console.error('Error polling audit status:', err);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [auditScanId, isAuditing]);

  const handleDiscoveryAudit = async () => {
    setIsAuditing(true);
    setAuditCompleted(false);
    setDiscoveredAssets([]);
    try {
      if (!userId) {
        console.error('No user ID found for audit');
        setIsAuditing(false);
        return;
      }
      const res = await initiateClassifiedAdsScan(userId);
      setAuditScanId(res.scan_id);
    } catch (err) {
      console.error('Error initiating audit:', err);
      setIsAuditing(false);
    }
  };

  const loadListings = async (jobId?: string) => {
    if (!userId) return;
    const idToUse = jobId || activeJobId;
    if (!idToUse) return;
    try {
      const data = await getClassifiedAdListingsByJob(idToUse);
      // Extract the General listing as the source copy
      const general = data.find(l => l.platform.toLowerCase() === 'general') || null;
      setGeneralListing(general);
    } catch (_) {}
  };

  const fetchLibrary = async () => {
    if (!userId) return;
    try {
      const data = await getClassifiedAdListings(userId);
      setLibraryListings(data);
    } catch (_) {}
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSaveLibraryUrl = async () => {
    if (!selectedLibraryAd) return;
    
    if (!editingLibraryUrl.trim() || !editingLibraryUrl.startsWith('http')) {
      setLibraryUrlError('Please enter a valid URL starting with http:// or https://');
      return;
    }
    
    setSavingLibraryUrl(true);
    setLibraryUrlError(null);
    try {
      const updated = await updateListingUrl(selectedLibraryAd.id, editingLibraryUrl.trim());
      setLibraryListings(prev => prev.map(l => l.id === updated.id ? updated : l));
      setSelectedLibraryAd(updated);
      setIsEditingUrl(false);
    } catch (err: any) {
      setLibraryUrlError(err.response?.data?.detail || 'Failed to update URL');
    } finally {
      setSavingLibraryUrl(false);
    }
  };

  const openLibraryModal = (ad: ClassifiedAdListing) => {
    setSelectedLibraryAd(ad);
    setEditingLibraryUrl(ad.listing_url || '');
    setIsEditingUrl(false);
    setLibraryUrlError(null);
  };

  // Load result/activeJobId from localStorage on mount
  useEffect(() => {
    if (!userId) return;
    const savedListing = localStorage.getItem(`classified_general_listing_${userId}`);
    const savedJobId = localStorage.getItem(`classified_active_job_id_${userId}`);
    if (savedListing) {
      try {
        const parsed = JSON.parse(savedListing);
        setGeneralListing(parsed);
        if (parsed.primary_keyword) {
          setKeyword(parsed.primary_keyword);
        }
      } catch (e) {}
    }
    if (savedJobId) {
      setActiveJobId(savedJobId);
    }
  }, [userId]);

  // Save result/activeJobId to localStorage when they change
  useEffect(() => {
    if (generalListing) {
      localStorage.setItem(`classified_general_listing_${userId}`, JSON.stringify(generalListing));
    } else {
      localStorage.removeItem(`classified_general_listing_${userId}`);
    }
  }, [generalListing, userId]);

  useEffect(() => {
    if (activeJobId) {
      localStorage.setItem(`classified_active_job_id_${userId}`, activeJobId);
    } else {
      localStorage.removeItem(`classified_active_job_id_${userId}`);
    }
  }, [activeJobId, userId]);

  // Sync checked platforms with DB generalListing published platforms
  useEffect(() => {
    if (generalListing) {
      const initialChecked: Record<string, boolean> = {};
      (generalListing.published_platforms || []).forEach((platformName: string) => {
        initialChecked[platformName] = true;
      });
      setCheckedPlatforms(initialChecked);
    } else {
      setCheckedPlatforms({});
    }
    setMarkCompleteSuccess(false);
  }, [generalListing]);

  const handleTogglePlatform = (platformName: string) => {
    setCheckedPlatforms(prev => ({
      ...prev,
      [platformName]: !prev[platformName],
    }));
    setMarkCompleteSuccess(false);
  };

  const handleMarkComplete = async () => {
    if (!generalListing) return;
    const selected = Object.keys(checkedPlatforms).filter(k => checkedPlatforms[k]);
    if (selected.length === 0) return;

    setIsMarkingComplete(true);
    try {
      const updated = await completeClassifiedAdListing(generalListing.id, { platforms: selected });
      setGeneralListing(updated);
      setMarkCompleteSuccess(true);
      setTimeout(() => setMarkCompleteSuccess(false), 3000);
      await fetchLibrary(); // Refresh the historical ad library
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update publication status');
    } finally {
      setIsMarkingComplete(false);
    }
  };


  const getPlatformGaps = () => {
    if (!platforms || platforms.length === 0) return [];
    
    const discoveredDomains = new Set(
      discoveredAssets.map((asset) => {
        const domain = asset.platform_domain || '';
        return domain.toLowerCase().replace('www.', '').trim();
      })
    );

    return platforms.filter((p) => {
      const platformDomain = (p.url || '').toLowerCase().replace('www.', '').trim();
      return !discoveredDomains.has(platformDomain);
    });
  };


  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!keyword.trim()) { setError('Please select or type a keyword.'); return; }
    setError(null);
    setLoading(true);
    setGenerating(true);
    setGeneralListing(null);
    setHasDefaultSelected(false);
    setCheckedPlatforms({});
    localStorage.removeItem(`classified_checklist_${userId}`);
    localStorage.removeItem(`classified_general_listing_${userId}`);
    localStorage.removeItem(`classified_active_job_id_${userId}`);
    try {
      // Always generate a single General ad — platform selection comes after
      const job = await generateClassifiedAdCopies({
        user_id: userId,
        selected_platforms: ['General'],
        keyword: keyword.trim(),
      });
      setActiveJobId(job.id);
      pollJobStatus(job.id);
    } catch (e: any) {
      setError(e?.message || 'Generation failed. Please try again.');
      setLoading(false);
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    try {
      await downloadSubmissionPackage(
        userId,
        activeJobId || generalListing?.job_id || undefined,
        generalListing?.id
      );
    } catch (e: any) {
      setError('Download failed. Please try again.');
    }
  };

  const handleSaveAdContent = async () => {
    if (!generalListing) return;
    setSavingAd(true);
    try {
      const updated = await updateListingContent(generalListing.id, {
        title: editTitle,
        description: editDesc,
      });
      setGeneralListing(updated);
      setIsEditingAd(false);
      fetchLibrary();
    } catch (e: any) {
      setError(e?.message || 'Failed to update ad content.');
    } finally {
      setSavingAd(false);
    }
  };

  const filteredKeywords = (prefill?.keywords || []).filter(k =>
    k.toLowerCase().includes(keywordSearch.toLowerCase())
  );

  const assetsList = libraryListings.filter(l => l.platform.toLowerCase() === 'general');

  useEffect(() => {
    if (libraryListings.length > 0 && !hasDefaultSelected) {
      const general = libraryListings.find(l => l.platform.toLowerCase() === 'general');
      if (general) {
        setGeneralListing(general);
        setHasDefaultSelected(true);
      }
    }
  }, [libraryListings, hasDefaultSelected]);





  // ── Layout ─────────────────────────────────────────────────────────────────
  return (
    <div className={`space-y-6 pb-24 ${darkMode ? 'dark text-white' : 'text-gray-900'}`}>
      {/* Back + Header */}
      <div>
        <button onClick={onBack} className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors mb-3 cursor-pointer border-none bg-transparent">
          <i className="ri-arrow-left-line mr-1.5" /> Back to Off-Page Content
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <i className="ri-newspaper-line text-blue-500" /> Classified Ad Generator
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              AI-powered classified ad copy. Generate once, post manually to high-DA platforms, track URLs here.
            </p>
          </div>

          {/* Website State Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1 border border-gray-200 dark:border-gray-700">
            {domainAgeLoading ? (
              <span className="px-4 py-2 text-xs text-gray-400 flex items-center gap-1.5"><i className="ri-loader-4-line animate-spin" /> Detecting…</span>
            ) : (
              <>
                <button
                  onClick={() => setMode('new_website')}
                  disabled={isNewWebsite === false}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center border-none ${
                    isNewWebsite === false ? 'opacity-50 cursor-not-allowed bg-transparent' : 'cursor-pointer'
                  } ${
                    mode === 'new_website'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-transparent'
                  }`}
                >
                  New Website
                  {isNewWebsite === false && <i className="ri-lock-line ml-1.5"></i>}
                </button>
                <button
                  onClick={() => setMode('existing_website')}
                  disabled={isNewWebsite === true}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center border-none ${
                    isNewWebsite === true ? 'opacity-50 cursor-not-allowed bg-transparent' : 'cursor-pointer'
                  } ${
                    mode === 'existing_website'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-transparent'
                  }`}
                >
                  Existing Website
                  {isNewWebsite === true && <i className="ri-lock-line ml-1.5"></i>}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {showLibrary ? (
        /* ── Ad Library view ─────────────────────────────────────────── */
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-blue-900 to-indigo-900 rounded-2xl p-6 text-white border border-blue-500/20 shadow-md relative overflow-hidden">
            <div className="absolute right-0 top-0 translate-x-8 -translate-y-8 text-[150px] text-blue-500/10 pointer-events-none">
              <i className="ri-archive-stack-line" />
            </div>
            <div className="flex items-center justify-between gap-4 relative z-10">
              <div>
                <h3 className="text-xl font-bold mb-1">Ad Library</h3>
                <p className="text-xs text-blue-200">History of all generated classified ads.</p>
              </div>
              <button onClick={fetchLibrary} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-xs font-bold transition-colors cursor-pointer">
                <i className="ri-refresh-line" /> Refresh
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm font-bold text-gray-800 dark:text-white">{libraryListings.length} ad{libraryListings.length !== 1 ? 's' : ''} saved</span>
            </div>
            {libraryListings.length === 0 ? (
              <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                <i className="ri-file-list-3-line text-4xl mb-3 block" />
                <p>No ads in your library yet.</p>
              </div>
            ) : (
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {libraryListings.map(ad => (
                  <div key={ad.id} onClick={() => openLibraryModal(ad)}
                    className="group border border-gray-200 dark:border-gray-700 rounded-xl p-5 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${PLATFORM_COLORS[ad.platform] || 'bg-gray-200'}`}>
                          <i className={`${PLATFORM_ICONS[ad.platform] || 'ri-global-line'}`} />
                        </div>
                        <span className="font-bold text-sm text-gray-900 dark:text-white capitalize">{ad.platform}</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${STATUS_BADGE[ad.status] || STATUS_BADGE.draft}`}>{ad.status}</span>
                    </div>
                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2 line-clamp-2">{ad.title}</p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-3 mb-4">{ad.description}</p>
                    <div className="flex items-center justify-between text-[10px] text-gray-400">
                      <span><i className="ri-key-2-line mr-1" />{ad.primary_keyword}</span>
                      <span>{new Date(ad.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedLibraryAd && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setSelectedLibraryAd(null)}>
              <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl relative" onClick={e => e.stopPropagation()}>
                <button onClick={() => setSelectedLibraryAd(null)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 border-none cursor-pointer">
                  <i className="ri-close-line text-lg" />
                </button>
                <div className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${PLATFORM_COLORS[selectedLibraryAd.platform] || 'bg-gray-200'}`}>
                      <i className={`${PLATFORM_ICONS[selectedLibraryAd.platform] || 'ri-global-line'}`} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white capitalize">{selectedLibraryAd.platform}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${STATUS_BADGE[selectedLibraryAd.status] || STATUS_BADGE.draft}`}>{selectedLibraryAd.status}</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border text-sm font-semibold text-gray-900 dark:text-white">{selectedLibraryAd.title}</div>
                    <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{selectedLibraryAd.description}</div>
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                      {!selectedLibraryAd.listing_url || isEditingUrl ? (
                        <div className="flex gap-2">
                          <input type="url" value={editingLibraryUrl} onChange={e => setEditingLibraryUrl(e.target.value)} placeholder="https://example.com/your-ad"
                            className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          <button onClick={handleSaveLibraryUrl} disabled={savingLibraryUrl}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-bold cursor-pointer disabled:opacity-50 border-none flex items-center gap-1">
                            {savingLibraryUrl ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-3-line" />} Save
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <a href={selectedLibraryAd.listing_url} target="_blank" rel="noreferrer" className="flex-1 flex items-center gap-2 text-blue-600 hover:underline bg-blue-50 dark:bg-blue-900/20 p-3 rounded-xl break-all text-sm font-medium border border-blue-100 dark:border-blue-900/40">
                            <i className="ri-external-link-line" /> {selectedLibraryAd.listing_url}
                          </a>
                          <button onClick={() => setIsEditingUrl(true)} className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm font-bold cursor-pointer border-none">Edit</button>
                        </div>
                      )}
                      {libraryUrlError && <p className="text-red-500 text-xs mt-1">{libraryUrlError}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ── Main generator view ─────────────────────────────────────── */
        <div className="space-y-6">
          {initialLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
              <i className="ri-loader-4-line text-4xl animate-spin text-blue-500 mb-4" />
              <p className="text-sm font-medium">Loading business profile...</p>
            </div>
          ) : (
            <>
              {/* Business Profile card */}
              {prefill && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-2xl p-5 flex flex-wrap justify-between items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-building-2-line text-blue-600 dark:text-blue-400 text-lg" />
                      <span className="font-semibold text-blue-800 dark:text-blue-200">Business Profile (Auto-filled from Onboarding)</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      {[
                        { label: 'Business Name', value: prefill.brand_name, icon: 'ri-store-2-line' },
                        { label: 'Website', value: prefill.website_url, icon: 'ri-global-line' },
                        { label: 'Phone', value: prefill.business_phone, icon: 'ri-phone-line' },
                        { label: 'Address', value: prefill.business_address, icon: 'ri-map-pin-2-line' },
                      ].map(item => (
                        <div key={item.label} className="flex items-start gap-2">
                          <i className={`${item.icon} text-blue-500 mt-0.5`} />
                          <div>
                            <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">{item.label}</div>
                            <div className="text-blue-900 dark:text-blue-100">{item.value || '—'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {isNewWebsite !== null && (
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${isNewWebsite ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                      {isNewWebsite ? 'New Domain' : 'Established Domain'}
                    </div>
                  )}
                </div>
              )}

              {/* ─── Audit Existing Classified Ads ─── */}
              {mode === 'existing_website' && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <i className="ri-radar-line text-blue-600 dark:text-blue-400"></i>
                        Audit Existing Classified Ads
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Use our crawler engine to discover and audit existing classified ads published for your brand across the web.
                      </p>
                    </div>
                    <button
                      onClick={handleDiscoveryAudit}
                      disabled={isAuditing}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 border-none cursor-pointer ${
                        isAuditing 
                          ? 'bg-gray-100 text-gray-55 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                      }`}
                    >
                      {isAuditing ? (
                        <><i className="ri-loader-4-line animate-spin"></i> Auditing...</>
                      ) : (
                        <><i className="ri-search-eye-line"></i> Run Discovery Audit</>
                      )}
                    </button>
                  </div>

                  {isAuditing && (
                    <div className="py-8 flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Crawling the web for existing classified ads...</p>
                    </div>
                  )}

                  {auditCompleted && discoveredAssets.length === 0 && (
                    <div className="py-6 text-center border-t border-gray-100 dark:border-gray-700 mt-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-55 dark:bg-gray-800 mb-3">
                        <i className="ri-search-line text-xl text-gray-400"></i>
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">No existing classified ads found</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                        No existing classified ads found. Our crawler couldn't find any listings published for your website.
                      </p>
                    </div>
                  )}

                  {auditCompleted && discoveredAssets.length > 0 && (
                    <div className="space-y-6 mt-4">
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-left border-collapse min-w-[1000px]">
                          <thead className="bg-gray-50 dark:bg-gray-800/50">
                            <tr>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Platform</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Title & Asset URL</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Live Status</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Title Keyword</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Word Count (150-300)</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Website Link</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">NAP Consistency</th>
                              <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Overall Score</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {discoveredAssets.map((asset) => {
                              const audit = asset.metadata_json?.audit;
                              const isLive = audit ? audit.is_live : (asset.status === 'ACTIVE');
                              const keywordInTitle = audit ? audit.keyword_in_title : null;
                              const matchedKeyword = audit ? audit.matched_keyword : null;
                              const wordCount = audit ? audit.word_count : null;
                              const wordCountCompliant = audit ? audit.word_count_compliant : null;
                              const websiteUrlFound = audit ? audit.website_url_found : null;
                              const napStatus = audit ? audit.nap_status : 'N/A';
                              const overallStatus = audit ? audit.status : (isLive ? 'Needs Attention' : 'Critical');
                              const notes = audit ? audit.notes : '';

                              const statusBg = (status: string) => {
                                switch (status) {
                                  case 'Optimal': return 'bg-emerald-50 dark:bg-emerald-950/20';
                                  case 'Needs Attention': return 'bg-amber-50 dark:bg-amber-955/20';
                                  case 'Critical': return 'bg-red-50 dark:bg-red-955/20';
                                  default: return 'bg-gray-50 dark:bg-gray-800';
                                }
                              };

                              const statusColor = (status: string) => {
                                switch (status) {
                                  case 'Optimal': return 'text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50';
                                  case 'Needs Attention': return 'text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-900/50';
                                  case 'Critical': return 'text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50';
                                  default: return 'text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700';
                                }
                              };

                              return (
                                <tr key={asset.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-sm">
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-7 h-7 rounded-lg border flex items-center justify-center flex-shrink-0 ${
                                        PLATFORM_COLORS[asset.platform_name || ''] || 'bg-gray-50 dark:bg-gray-800 border-gray-200 text-gray-500'
                                      }`}>
                                        <i className={`${PLATFORM_ICONS[asset.platform_name || ''] || 'ri-global-line'} text-sm`}></i>
                                      </div>
                                      <span className="font-semibold text-gray-900 dark:text-white capitalize">
                                        {asset.platform_name || asset.platform_domain || 'Unknown'}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 max-w-[250px]">
                                    <div className="font-medium text-gray-900 dark:text-white truncate" title={asset.asset_title || ''}>
                                      {asset.asset_title || 'Untitled Listing'}
                                    </div>
                                    <a
                                      href={asset.asset_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 dark:text-blue-400 font-mono hover:underline flex items-center gap-0.5 truncate mt-0.5"
                                    >
                                      {asset.asset_url} <i className="ri-external-link-line text-[9px]"></i>
                                    </a>
                                  </td>
                                  <td className="py-3 px-4">
                                    {isLive ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50">
                                        Live
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-50 dark:bg-red-955/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50" title={notes}>
                                        Dead
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    {audit ? (
                                      keywordInTitle ? (
                                        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold" title={`Matched keyword: "${matchedKeyword}"`}>
                                          <i className="ri-checkbox-circle-line"></i> Yes
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 text-red-650 text-red-550 text-red-600 dark:text-red-400 font-semibold">
                                          <i className="ri-error-warning-line"></i> No
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500">—</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    {audit ? (
                                      <div className="flex flex-col gap-0.5">
                                        <span className="font-semibold text-gray-800 dark:text-gray-250">{wordCount} words</span>
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded w-max font-bold uppercase tracking-wider ${
                                          wordCountCompliant
                                            ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/40'
                                            : 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border border-red-100 dark:border-red-900/40'
                                        }`}>
                                          {wordCountCompliant ? 'Compliant' : 'Non-compliant'}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500">—</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    {audit ? (
                                      websiteUrlFound ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50">
                                          Found
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-50 dark:bg-red-955/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50">
                                          Missing
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500">—</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    {audit ? (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${
                                        napStatus === 'Consistent'
                                          ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50'
                                          : napStatus === 'Inconsistent'
                                          ? 'bg-red-50 dark:bg-red-955/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50'
                                          : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                                      }`}>
                                        {napStatus}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400 dark:text-gray-500">—</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-bold ${statusBg(overallStatus)} ${statusColor(overallStatus)}`}>
                                      {overallStatus}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Platform Gaps Panel */}
                      {(() => {
                        const gaps = getPlatformGaps();
                        if (gaps.length === 0) return null;
                        return (
                          <div className="p-5 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/20 dark:bg-amber-955/5">
                            <div className="flex items-start gap-3">
                              <i className="ri-error-warning-line text-amber-600 dark:text-amber-500 text-lg flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="text-sm font-bold text-gray-900 dark:text-white">Platform Checklist Gaps Detected</h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Your backlink profile is currently missing active Classified Ads on these suggested platforms:
                                </p>
                                <div className="flex flex-wrap gap-2 mt-3">
                                  {gaps.map((p) => (
                                    <span key={p.name} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                                      <div className={`w-4.5 h-4.5 rounded border flex items-center justify-center flex-shrink-0 ${PLATFORM_COLORS[p.name] || 'bg-gray-100 text-gray-55 text-gray-500'}`}>
                                        <i className={`${PLATFORM_ICONS[p.name] || 'ri-global-line'} text-[10px]`} />
                                      </div>
                                      {p.display} (DA {p.da})
                                    </span>
                                  ))}
                                </div>
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 italic">
                                  Tip: Switch to the "New Website" view above to generate and submit Classified Ads to these platforms to fill this gap.
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

                  {/* Keyword selection */}
                  {(prefill?.keywords?.length ?? 0) > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                    <i className="ri-key-2-line text-blue-500" /> Select Primary Keyword
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Choose the keyword to anchor your classified ad copy.</p>
                  <input type="text" value={keywordSearch} onChange={e => setKeywordSearch(e.target.value)} placeholder="Search keywords..."
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-3" />
                  <div className="flex flex-wrap gap-2">
                    {filteredKeywords.slice(0, 20).map(kw => (
                      <button key={kw} onClick={() => setKeyword(kw)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-colors ${keyword === kw ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400'}`}>
                        {kw}
                      </button>
                    ))}
                  </div>
                </div>
              )}


              {/* Target platforms preview */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <i className="ri-global-line text-blue-500" /> Suggested Platforms
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {platforms.map(p => (
                    <div key={p.name} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 text-center border border-gray-100 dark:border-gray-700">
                      <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mx-auto mb-2 ${PLATFORM_COLORS[p.name] || 'bg-gray-100'}`}>
                        <i className={`${PLATFORM_ICONS[p.name] || 'ri-global-line'} text-xl`} />
                      </div>
                      <div className="text-xs font-semibold text-gray-900 dark:text-white">{p.display}</div>
                      <div className="text-xs text-gray-400 mt-0.5">DA {p.da}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                  <i className="ri-error-warning-line text-red-500 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              {/* Generate button */}
              <button onClick={handleGenerate} disabled={generating || !keyword.trim()}
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-base flex items-center justify-center gap-3 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-200 dark:shadow-blue-900/30 border-none cursor-pointer">
                <i className="ri-sparkling-2-line text-xl" />
                {generating ? 'Generating Ad Copy…' : generalListing ? 'Regenerate Ad Copy' : 'Generate Ad Copy'}
              </button>

              {/* ── Generated Ads Gallery & Selection Pane ── */}
              {assetsList.length > 0 && !generating && (
                <div className="space-y-6 mt-8">
                  {/* Generated Ads Gallery Grid */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <h3 className="text-lg font-bold mb-1">Generated Classified Ads</h3>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Click an ad to view details, track submission status, and download its individual package below.</p>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {assetsList.map((asset) => {
                        const isSelected = generalListing?.id === asset.id;
                        return (
                          <div
                            key={asset.id}
                            onClick={() => setGeneralListing(asset)}
                            className={`group relative cursor-pointer rounded-xl overflow-hidden border transition-all bg-gray-55 dark:bg-gray-900/50 hover:shadow-md ${
                              isSelected
                                ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900/30'
                                : 'border-gray-200 dark:border-gray-700 hover:border-blue-400'
                            }`}
                          >
                            <div className="aspect-square bg-gray-100 dark:bg-gray-900 relative overflow-hidden flex items-center justify-center">
                              {asset.image_url ? (
                                <img
                                  src={getListingImageProxyUrl(asset.id)}
                                  alt={asset.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                              ) : (
                                <div className="w-full h-full flex flex-col items-center justify-center p-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 text-gray-400 dark:text-gray-500">
                                  <i className="ri-file-text-line text-3xl mb-1 text-gray-300 dark:text-gray-600" />
                                  <span className="text-[10px] font-semibold uppercase">Text Only</span>
                                </div>
                              )}
                              <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 bg-black/70 text-white rounded text-[8px] font-bold uppercase">
                                {asset.word_count} Words
                              </span>
                            </div>
                            <div className="p-3">
                              <p className="text-xs font-bold text-gray-900 dark:text-white truncate" title={asset.title}>
                                {asset.title}
                              </p>
                              <div className="flex items-center gap-1 mt-1 text-[10px] text-gray-550 dark:text-gray-400">
                                <i className="ri-key-2-line text-blue-500" />
                                <span className="truncate">{asset.primary_keyword}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>



                  {/* ── Split-pane: Selected ad details (left) + Platform checklist (right) ── */}
                  {generalListing && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

                  {/* ── LEFT PANE: Image + Ad Copy ─────────────────────────────── */}
                  <div className="flex flex-col gap-4">

                    {/* Generated Image Card */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                      {generalListing.image_url ? (
                        <div className="relative group">
                          <img
                            src={getListingImageProxyUrl(generalListing.id)}
                            alt={`Classified ad for ${generalListing.primary_keyword}`}
                            className="w-full h-auto object-contain bg-gray-50 dark:bg-gray-900 rounded-t-2xl"
                            style={{ maxHeight: '440px', objectPosition: 'center' }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-t-2xl" />
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center gap-3 py-10 px-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-t-2xl border-b border-gray-200 dark:border-gray-700">
                          <div className="w-14 h-14 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-sm">
                            <i className="ri-image-line text-2xl text-gray-300 dark:text-gray-600" />
                          </div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 text-center">
                            Text ad generated successfully.
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                            Image enhancement is not available for this listing.
                          </p>
                        </div>
                      )}

                      {/* Ad copy below image */}
                      <div className="p-5">
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                          <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">✓ Generated</span>
                          <span className="px-2 py-0.5 rounded-md text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            <i className="ri-key-2-line mr-1" />{generalListing.primary_keyword}
                          </span>
                          <span className="text-xs text-gray-500"><i className="ri-text-wrap mr-1" />{generalListing.word_count} words</span>
                        </div>

                        {isEditingAd ? (
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Ad Title"
                            />
                            <textarea
                              value={editDesc}
                              onChange={(e) => setEditDesc(e.target.value)}
                              rows={7}
                              className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Ad Description"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={handleSaveAdContent}
                                disabled={savingAd}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-xs font-semibold cursor-pointer border-none flex justify-center items-center gap-2"
                              >
                                {savingAd ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-3-line" />} Save Changes
                              </button>
                              <button
                                onClick={() => setIsEditingAd(false)}
                                disabled={savingAd}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded-lg px-4 py-2 text-xs font-semibold cursor-pointer border-none"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex justify-between items-start gap-4 mb-2">
                              <h3 className="font-bold text-sm text-gray-900 dark:text-white">{generalListing.title}</h3>
                              <button
                                onClick={() => {
                                  setEditTitle(generalListing.title);
                                  setEditDesc(generalListing.description);
                                  setIsEditingAd(true);
                                  setAdExpanded(true);
                                }}
                                className="text-gray-400 hover:text-blue-500 transition-colors bg-transparent border-none cursor-pointer flex-shrink-0"
                                title="Edit Ad"
                              >
                                <i className="ri-pencil-line text-lg" />
                              </button>
                            </div>
                            {adExpanded && (
                              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap mb-3">
                                {generalListing.description}
                              </div>
                            )}
                            <button onClick={() => setAdExpanded(v => !v)}
                              className="w-full rounded-lg px-4 py-2.5 text-xs font-semibold border flex items-center justify-center gap-2 transition-colors cursor-pointer border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 bg-transparent">
                              <i className={`ri-${adExpanded ? 'eye-off' : 'eye'}-line`} /> {adExpanded ? 'Hide' : 'View Full Ad Copy'}
                            </button>
                            {adExpanded && (
                              <button onClick={() => navigator.clipboard.writeText(generalListing.title + '\n\n' + generalListing.description)}
                                className="w-full mt-2 rounded-lg px-4 py-2 text-xs font-semibold border flex items-center justify-center gap-2 transition-colors cursor-pointer border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 bg-transparent">
                                <i className="ri-file-copy-line" /> Copy Ad Copy
                              </button>
                            )}
                          </>
                        )}

                        {/* Download ZIP */}
                        <button onClick={handleDownload}
                          className="mt-4 w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-xl flex items-center justify-center gap-1.5 cursor-pointer border-none transition-colors">
                          <i className="ri-download-2-line" /> Download ZIP (Text + Image)
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ── RIGHT PANE: Platform Checklist ─────────────────────────── */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                          <i className="ri-global-line text-blue-500" /> Suggested Platforms — Submission Checklist
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Copy the ad above and post it manually on each platform. Check them off once done.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {markCompleteSuccess && (
                          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                            <i className="ri-checkbox-circle-fill text-base" /> Saved!
                          </span>
                        )}
                        <button
                          onClick={handleMarkComplete}
                          disabled={isMarkingComplete || !Object.values(checkedPlatforms).some(Boolean)}
                          className={`px-4 py-2 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all shadow-sm cursor-pointer border-none ${
                            Object.values(checkedPlatforms).some(Boolean)
                              ? 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed shadow-none'
                          }`}
                        >
                          {isMarkingComplete ? (
                            <><i className="ri-loader-4-line animate-spin" /> Saving...</>
                          ) : (
                            <><i className="ri-checkbox-circle-line" /> Mark as Complete</>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {platforms.map(p => {
                        const isChecked = !!checkedPlatforms[p.name];
                        return (
                          <div
                            key={p.name}
                            className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isChecked ? 'bg-emerald-50/30 dark:bg-emerald-950/10 border-emerald-100 dark:border-emerald-900/50' : darkMode ? 'bg-gray-900/50 border-gray-800' : 'bg-gray-55 border-gray-100'}`}
                          >
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleTogglePlatform(p.name)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                              />
                              <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${PLATFORM_COLORS[p.name] || 'bg-gray-100'}`}>
                                <i className={`${PLATFORM_ICONS[p.name] || 'ri-global-line'} text-base`} />
                              </div>
                              <div>
                                <span className={`text-sm font-semibold text-gray-900 dark:text-white capitalize ${isChecked ? 'line-through text-gray-400 dark:text-gray-500 font-medium' : ''}`}>{p.display}</span>
                                <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700`}>DA {p.da}</span>
                              </div>
                            </div>
                            <a
                              href={`https://${p.url || p.display.toLowerCase() + '.com'}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-semibold"
                            >
                              Visit Site <i className="ri-external-link-line" />
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                    </div>
                  )}
                </div>
              )}

              {/* Generating spinner */}
              {generating && (
                <div className="flex flex-col items-center justify-center py-16 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                  <i className="ri-loader-4-line text-4xl animate-spin text-blue-500 mb-4" />
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Generating your classified ad copy…</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
